import { useQuest } from "@/context/QuestProvider";
import { UPDATE_QUEST_GROUP } from "@/context/QuestProvider/actions";
import type { LatestEarnings } from "@/domain/gems-game/domain";
import { computeQuestStatusFields } from "@hooks/quest/useInitialiseQuestGroup/transform/computeQuestStatusFields";
import { createQuestOrder } from "@hooks/quest/useOrderingQuest/transform/createQuestOrder";
import type { Content } from "@prismicio/client";
import type { Quest, QuestGroupState, QuestState } from "@ui-kit/QuestList";
import localforage from "localforage";
import { useCallback } from "react";
import { advanceQuestToNextGroup } from "./transform/advanceQuestToNextGroup";

export const useSkipQuest = (
  walletAddress: string | undefined,
  game: Content.GameDocument,
  latestEarnings: LatestEarnings | undefined,
) => {
  const { state: questGroupState, dispatchAction } = useQuest((store) => store);

  const storageKey = `${game.uid}_quest_order_${walletAddress}`;

  // Enrich quests with additional fields.
  const updateQuestGroupWithQuestStatus = useCallback(
    (questGroupState: QuestGroupState[]) => {
      return questGroupState.map((currentGroup, groupIndex) => ({
        ...currentGroup,
        quests: currentGroup.quests.map(
          (currentQuest: Quest, questIndex: number) => {
            const timeAndStatus = computeQuestStatusFields(
              questGroupState,
              currentGroup,
              groupIndex,
              currentQuest,
              questIndex,
              latestEarnings,
            );
            return { ...currentQuest, ...timeAndStatus } as QuestState;
          },
        ),
      })) as QuestGroupState[];
    },
    [latestEarnings],
  );

  const skipQuest = useCallback(
    async (quest: Quest) => {
      if (!walletAddress && !quest) return;
      // Call the transform function to modify the quest state
      const { questGroups, skipped } = advanceQuestToNextGroup(
        questGroupState,
        quest.ruleId,
      );

      const newQuestGroups = updateQuestGroupWithQuestStatus(questGroups);

      if (skipped) {
        dispatchAction({
          type: UPDATE_QUEST_GROUP,
          payload: { id: "skip-quests", questGroups: newQuestGroups },
        });

        // Transform the quest groups to quest order object before saving to storage
        const transformedQuestGroups = createQuestOrder(questGroups);
        try {
          await localforage.setItem(storageKey, transformedQuestGroups);
        } catch (error) {
          console.error("Error saving quest groups:", error);
        }
      }
    },
    [
      dispatchAction,
      questGroupState,
      storageKey,
      walletAddress,
      updateQuestGroupWithQuestStatus,
    ],
  );

  return { skipQuest };
};
